import * as THREE from 'three/webgpu';
import { HALF_W, WHEEL_BASE_HALF, WHEEL_RADIUS, WHEELS } from './jeep.js';

export function createJeepModel() {
  const root = new THREE.Group();

  // Root contains chassisGroup and independent wheel mount groups
  const chassisGroup = new THREE.Group();
  root.add(chassisGroup);

  // Materials (WebGPU NodeMaterials)
  const matBody = new THREE.MeshStandardNodeMaterial({
    color: 0xed8936, // Vivid rally orange
    roughness: 0.32,
    metalness: 0.65
  });

  const matDark = new THREE.MeshStandardNodeMaterial({
    color: 0x1a202c, // Matte charcoal trim & bumpers
    roughness: 0.75,
    metalness: 0.2
  });

  const matGlass = new THREE.MeshStandardNodeMaterial({
    color: 0x0f172a, // Tinted windscreen
    roughness: 0.1,
    metalness: 0.9,
    transparent: true,
    opacity: 0.75
  });

  const matChrome = new THREE.MeshStandardNodeMaterial({
    color: 0xe2e8f0,
    roughness: 0.2,
    metalness: 0.95
  });

  const matHeadlight = new THREE.MeshBasicNodeMaterial({
    color: 0xfffbeb
  });

  const matTaillight = new THREE.MeshBasicNodeMaterial({
    color: 0xdc2626
  });

  const matExhaust = new THREE.MeshBasicNodeMaterial({
    color: 0x06b6d4,
    transparent: true,
    opacity: 0.0
  });

  // -------------------------------------------------------------------------
  // 1. Chassis Bodywork
  // -------------------------------------------------------------------------
  // Lower tub
  const tubGeom = new THREE.BoxGeometry(3.6, 0.45, 1.82);
  tubGeom.translate(0, 0.3, 0);
  const tub = new THREE.Mesh(tubGeom, matBody);
  tub.castShadow = true;
  chassisGroup.add(tub);

  // Hood / bonnet
  const hoodGeom = new THREE.BoxGeometry(1.35, 0.35, 1.6);
  hoodGeom.translate(1.05, 0.62, 0);
  const hood = new THREE.Mesh(hoodGeom, matBody);
  hood.castShadow = true;
  chassisGroup.add(hood);

  // Cabin cockpit
  const cabinGeom = new THREE.BoxGeometry(1.65, 0.65, 1.5);
  cabinGeom.translate(-0.45, 0.85, 0);
  const cabin = new THREE.Mesh(cabinGeom, matDark);
  cabin.castShadow = true;
  chassisGroup.add(cabin);

  // Windshield (angled)
  const windGeom = new THREE.BoxGeometry(0.12, 0.58, 1.4);
  const windshield = new THREE.Mesh(windGeom, matGlass);
  windshield.position.set(0.38, 0.86, 0);
  windshield.rotation.z = -0.42;
  chassisGroup.add(windshield);

  // Side windows
  const sideWinGeom = new THREE.BoxGeometry(1.2, 0.38, 1.54);
  sideWinGeom.translate(-0.45, 0.88, 0);
  const sideWindows = new THREE.Mesh(sideWinGeom, matGlass);
  chassisGroup.add(sideWindows);

  // Roll cage bars (tubular steel)
  const rollBarGeom = new THREE.CylinderGeometry(0.045, 0.045, 1.5, 8);
  rollBarGeom.rotateX(Math.PI / 2);
  const rollBar1 = new THREE.Mesh(rollBarGeom, matDark);
  rollBar1.position.set(-0.45, 1.2, 0);
  chassisGroup.add(rollBar1);

  // Front & rear heavy bumpers
  const ffbGeom = new THREE.BoxGeometry(0.3, 0.28, 2.05);
  ffbGeom.translate(1.9, 0.25, 0);
  const fBumper = new THREE.Mesh(ffbGeom, matDark);
  fBumper.castShadow = true;
  chassisGroup.add(fBumper);

  const rfbGeom = new THREE.BoxGeometry(0.3, 0.28, 2.05);
  rfbGeom.translate(-1.88, 0.25, 0);
  const rBumper = new THREE.Mesh(rfbGeom, matDark);
  rBumper.castShadow = true;
  chassisGroup.add(rBumper);

  // Front grille
  const grilleGeom = new THREE.BoxGeometry(0.06, 0.32, 1.2);
  const grille = new THREE.Mesh(grilleGeom, matDark);
  grille.position.set(1.75, 0.62, 0);
  chassisGroup.add(grille);

  // Headlights
  const hlGeom = new THREE.CylinderGeometry(0.14, 0.14, 0.08, 12);
  hlGeom.rotateZ(Math.PI / 2);
  const hlL = new THREE.Mesh(hlGeom, matHeadlight);
  hlL.position.set(1.76, 0.62, 0.65);
  const hlR = new THREE.Mesh(hlGeom, matHeadlight);
  hlR.position.set(1.76, 0.62, -0.65);
  chassisGroup.add(hlL);
  chassisGroup.add(hlR);

  // Taillights
  const tlGeom = new THREE.BoxGeometry(0.06, 0.16, 0.28);
  const tlL = new THREE.Mesh(tlGeom, matTaillight);
  tlL.position.set(-1.82, 0.55, 0.72);
  const tlR = new THREE.Mesh(tlGeom, matTaillight);
  tlR.position.set(-1.82, 0.55, -0.72);
  chassisGroup.add(tlL);
  chassisGroup.add(tlR);

  // Nitro exhaust pipes (dual)
  const exhGeom = new THREE.CylinderGeometry(0.07, 0.07, 0.22, 10);
  exhGeom.rotateZ(Math.PI / 2);
  const exhL = new THREE.Mesh(exhGeom, matChrome);
  exhL.position.set(-1.95, 0.15, 0.45);
  const exhR = new THREE.Mesh(exhGeom, matChrome);
  exhR.position.set(-1.95, 0.15, -0.45);
  chassisGroup.add(exhL);
  chassisGroup.add(exhR);

  // Nitro flame plumes
  const flameGeom = new THREE.ConeGeometry(0.12, 0.6, 8);
  flameGeom.rotateZ(-Math.PI / 2);
  const flameL = new THREE.Mesh(flameGeom, matExhaust);
  flameL.position.set(-2.28, 0.15, 0.45);
  const flameR = new THREE.Mesh(flameGeom, matExhaust);
  flameR.position.set(-2.28, 0.15, -0.45);
  chassisGroup.add(flameL);
  chassisGroup.add(flameR);

  // Rear tailgate spare tire
  const spareGeom = new THREE.CylinderGeometry(0.42, 0.42, 0.26, 16);
  spareGeom.rotateZ(Math.PI / 2);
  const spare = new THREE.Mesh(spareGeom, matDark);
  spare.position.set(-1.98, 0.65, 0);
  spare.castShadow = true;
  chassisGroup.add(spare);

  // -------------------------------------------------------------------------
  // 2. Wheels & Suspension Struts
  // -------------------------------------------------------------------------
  const wheelMeshes = [];
  const tireGeom = new THREE.CylinderGeometry(WHEEL_RADIUS, WHEEL_RADIUS, 0.36, 18);
  tireGeom.rotateX(Math.PI / 2);

  const rimGeom = new THREE.CylinderGeometry(WHEEL_RADIUS * 0.62, WHEEL_RADIUS * 0.62, 0.38, 12);
  rimGeom.rotateX(Math.PI / 2);

  const springGeom = new THREE.CylinderGeometry(0.06, 0.06, 0.6, 8);

  for (let i = 0; i < 4; i++) {
    const wDef = WHEELS[i];

    // Hub node: stays fixed in XZ relative to chassis, moves up/down with suspension
    const hub = new THREE.Group();
    hub.position.set(wDef.x, 0, wDef.z);
    root.add(hub);

    // Suspension shock strut (visual cylinder connecting chassis to hub)
    const strut = new THREE.Mesh(springGeom, matChrome);
    strut.position.set(0, 0.25, 0);
    hub.add(strut);

    // Steering group (rotates around Y for front wheels)
    const steerPivot = new THREE.Group();
    hub.add(steerPivot);

    // Spin group (rotates around Z for wheel rolling)
    const spinPivot = new THREE.Group();
    steerPivot.add(spinPivot);

    // Tire
    const tire = new THREE.Mesh(tireGeom, matDark);
    tire.castShadow = true;
    spinPivot.add(tire);

    // Rim
    const rim = new THREE.Mesh(rimGeom, matChrome);
    spinPivot.add(rim);

    wheelMeshes.push({
      hub,
      strut,
      steerPivot,
      spinPivot,
      isSteered: wDef.steered,
      x: wDef.x,
      z: wDef.z
    });
  }

  return {
    root,
    chassisGroup,
    wheelMeshes,
    matTaillight,
    matExhaust,
    flameL,
    flameR
  };
}

export function updateJeepVisuals(model, jeep, input) {
  // 1. Root position and yaw
  model.root.position.set(jeep.pos.x, jeep.pos.y, jeep.pos.z);
  model.root.rotation.set(0, jeep.yaw, 0);

  // 2. Chassis roll and pitch articulation
  const pose = jeep.visualPose();
  model.chassisGroup.rotation.x = pose.pitch;
  model.chassisGroup.rotation.z = -pose.roll;

  // 3. Wheel positions, steering angle, and rolling spin
  for (let i = 0; i < 4; i++) {
    const wm = model.wheelMeshes[i];
    // Local hub vertical position based on suspension travel relative to chassis
    // REST_GAP = 0.38 m. When compressed, hub moves upwards (+Y).
    const comp = jeep.suspension[i];
    const restComp = 0.24;
    const dy = (comp - restComp);
    wm.hub.position.y = dy;

    // Front wheel steering
    if (wm.isSteered) {
      wm.steerPivot.rotation.y = jeep.steer;
    }

    // Wheel spin
    wm.spinPivot.rotation.z = -jeep.wheelSpin;
  }

  // 4. Taillight brightness on braking / handbrake
  const isBraking = (input.brake && jeep.speed > 0.4) || input.handbrake;
  if (isBraking) {
    model.matTaillight.color.setHex(0xff1e1e);
  } else {
    model.matTaillight.color.setHex(0x7f1d1d);
  }

  // 5. Nitro flames visibility
  if (jeep.boosting) {
    model.matExhaust.opacity = 0.95;
    const flicker = 0.8 + Math.random() * 0.4;
    model.flameL.scale.set(flicker, flicker * 1.3, flicker);
    model.flameR.scale.set(flicker, flicker * 1.3, flicker);
  } else {
    model.matExhaust.opacity = 0.0;
  }
}
